import React from 'react';
import { Check, Award, GraduationCap, Heart, Users, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { aboutContent, certificates } from '../data/mock';

const AboutPage = () => {
  return (
    <div>
      {/* Page Header */}
      <section className="bg-teal-700 py-16 md:py-20">
        <div className="container mx-auto px-6">
          <div className="text-center text-white">
            <p className="text-sand-200 font-medium tracking-[0.3em] uppercase text-xs mb-2">Обо мне</p>
            <h1 className="text-4xl md:text-5xl font-serif">Лазарева Ирина Антоновна</h1>
            <p className="mt-4 text-sand-100 max-w-2xl mx-auto">
              Клинический психолог с более чем 30-летним опытом работы
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src={aboutContent.image}
                    alt="Психолог Ирина Лазарева"
                    width="800"
                    height="500"
                    loading="eager"
                    decoding="async"
                    className="h-[360px] w-full object-cover md:h-[500px]"
                  />
                </div>
                <div className="absolute bottom-4 right-4 rounded-2xl bg-teal-600 p-4 text-white shadow-xl md:-bottom-6 md:-right-6 md:p-6">
                  <p className="text-4xl font-serif font-bold">30+</p>
                  <p className="text-teal-100 text-sm">Лет практики</p>
                </div>
                <div className="absolute -left-6 -top-6 -z-10 hidden h-full w-full rounded-3xl border-2 border-teal-200 md:block" />
              </div>
            </div>

            {/* Content */}
            <div className="space-y-6">
              <div>
                <p className="text-teal-600 font-medium tracking-wide uppercase text-sm mb-2">
                  О специалисте
                </p>
                <h2 className="text-3xl md:text-4xl font-serif text-stone-800 leading-tight">
                  Получите правильную помощь от того, кто заботится
                </h2>
              </div>

              <p className="text-stone-600 leading-relaxed text-lg">
                {aboutContent.description}
              </p>
              <p className="text-stone-600 leading-relaxed">
                {aboutContent.additionalInfo}
              </p>

              {/* Highlights */}
              <ul className="space-y-3">
                {aboutContent.highlights.map((highlight, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="text-teal-600" size={14} />
                    </div>
                    <span className="text-stone-700">{highlight}</span>
                  </li>
                ))}
              </ul>

              <Link to="/appointment">
                <Button className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 rounded-md text-base font-medium mt-4">
                  Записаться на консультацию
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 md:py-16 bg-sand-50 section-contrast">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center bg-white border border-sand-200 rounded-2xl p-6 shadow-sm">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="text-teal-600" size={28} />
              </div>
              <p className="text-3xl font-serif text-teal-600 font-bold">30+</p>
              <p className="text-stone-500 mt-1">Лет опыта</p>
            </div>
            <div className="text-center bg-white border border-sand-200 rounded-2xl p-6 shadow-sm">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-teal-600" size={28} />
              </div>
              <p className="text-3xl font-serif text-teal-600 font-bold">1200+</p>
              <p className="text-stone-500 mt-1">Клиентов</p>
            </div>
            <div className="text-center bg-white border border-sand-200 rounded-2xl p-6 shadow-sm">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="text-teal-600" size={28} />
              </div>
              <p className="text-3xl font-serif text-teal-600 font-bold">3500+</p>
              <p className="text-stone-500 mt-1">Консультаций</p>
            </div>
            <div className="text-center bg-white border border-sand-200 rounded-2xl p-6 shadow-sm">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="text-teal-600" size={28} />
              </div>
              <p className="text-3xl font-serif text-teal-600 font-bold">24/7</p>
              <p className="text-stone-500 mt-1">Онлайн поддержка</p>
            </div>
          </div>
        </div>
      </section>

      {/* Certificates */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-10 md:mb-12">
            <p className="text-teal-600 font-medium tracking-wide uppercase text-sm mb-2">
              Квалификация
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-stone-800">
              Образование и сертификаты
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certificates.map((cert) => (
              <div
                key={cert.id}
                className="bg-sand-50 hover:bg-white hover:shadow-lg rounded-2xl p-6 transition-all duration-300 border border-sand-200"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="text-teal-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-stone-800 mb-1">{cert.title}</h4>
                    <p className="text-teal-600 text-sm font-medium">{cert.institution}</p>
                    <p className="text-stone-500 text-sm mt-1">{cert.specialization}</p>
                    <p className="text-stone-400 text-xs mt-2">{cert.year}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center md:mt-12">
            <Link to="/appointment">
              <Button className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 rounded-md text-base font-medium">
                Записаться на консультацию
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
